import React from "react";
import {
  Box, Select, MenuItem, TextField, IconButton, Button, ToggleButton,
  ToggleButtonGroup, Typography, Alert, Autocomplete, Chip, FormControl, InputLabel
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { OPERATOR_LABELS } from "../engine/logicEngine";
import { FIELD_TYPE_MAP, LAYOUT_FIELD_TYPES, updateAt } from "../formsUtils";

const NO_VALUE_OPS = ["is_empty", "is_not_empty"];
const MULTI_VALUE_OPS = ["in", "not_in"];

const COR = {
  primary: "#3f51b5",
  primarySoft: "#eceefa",
  primaryBorder: "#c9cfee",
  borderSubtle: "#ededf0",
  warning: "#b54708",
  warningBorder: "#f5c27a",
  textMuted: "#6b6c72",
};

/** Operadores que fazem sentido para o tipo da pergunta. */
function operadoresDoCampo(field) {
  if (!field) return Object.keys(OPERATOR_LABELS);
  if (field.type === "checkbox") return ["contains", "not_contains", "is_empty", "is_not_empty"];
  if (Array.isArray(field.options)) return ["eq", "neq", "in", "not_in", "is_empty", "is_not_empty"];
  if (["number", "scale"].includes(field.type)) return ["eq", "neq", "gt", "gte", "lt", "lte", "is_empty", "is_not_empty"];
  return ["eq", "neq", "contains", "not_contains", "starts_with", "ends_with", "is_empty", "is_not_empty"];
}

function rotuloDoCampo(f) {
  if (!f) return "";
  return f.label ? f.label : `(sem rótulo) ${f.name || f.id}`;
}

/**
 * Editor de uma condição composta de um nível: { op: 'and'|'or', rules: [leaf] }
 * onde leaf = { field, operator, value }.
 *
 * Props: condition, fields (lista de campos do form), onChange(condition|null).
 */
export default function ConditionBuilder({ condition, fields, onChange }) {
  const selectableFields = (fields || []).filter(
    (f) => !LAYOUT_FIELD_TYPES.includes(f.type)
  );

  if (!condition) {
    return (
      <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() =>
        onChange({ op: "and", rules: [emptyRule()] })
      }>
        Adicionar condição
      </Button>
    );
  }

  // Espelha a decisão do evaluateCondition. Há formulário publicado com regra solta no topo
  // ({field, operator, value}), e o editor antigo ainda gravava `rules` ou `op` ao lado dela:
  // o motor avalia a regra do topo nesses casos. Ler só `rules` aqui mostrava painel vazio ou
  // uma regra que não vale, e a próxima edição descartava a regra real em silêncio.
  const temOp = condition.op === "and" || condition.op === "or";
  const ehGrupo = Array.isArray(condition.rules) && (temOp || !condition.field);
  const group = ehGrupo
    ? condition
    : {
        op: condition.op === "or" ? "or" : "and",
        rules: condition.field
          ? [{ field: condition.field, operator: condition.operator, value: condition.value }]
          : [],
      };

  const rules = group.rules;
  const conector = group.op === "or" ? "OU" : "E";

  const update = (patch) => onChange({ ...group, ...patch });
  const updateRule = (idx, patch) => update({ rules: updateAt(rules, idx, patch) });
  const removeRule = (idx) => {
    const next = rules.filter((_, i) => i !== idx);
    if (next.length === 0) return onChange(null);
    update({ rules: next });
  };

  return (
    <Box sx={{ border: `1px solid ${COR.borderSubtle}`, borderRadius: "10px", p: 2, mt: 1 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, flexWrap: "wrap" }}>
        <Typography variant="body2">Mostrar quando</Typography>
        <ToggleButtonGroup size="small" exclusive value={group.op}
          onChange={(_, v) => v && update({ op: v })}>
          <ToggleButton value="and" sx={{ textTransform: "none" }}>todas as regras</ToggleButton>
          <ToggleButton value="or" sx={{ textTransform: "none" }}>qualquer regra</ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="body2">forem verdadeiras</Typography>
      </Box>

      {rules.map((rule, idx) => (
        <React.Fragment key={idx}>
          {idx > 0 && <Conector texto={conector} />}
          {rule && Array.isArray(rule.rules) ? (
            // Subgrupo aninhado (o motor suporta, este editor é de um nível só): editá-lo aqui
            // gravaria field/operator por cima e perderia as regras filhas. Mostra e não toca.
            <Alert severity="warning">
              Subgrupo de condições ({(rule.op || "and").toUpperCase()}, {rule.rules.length} regra(s)) —
              não editável aqui. Ajuste pelo JSON (botão JSON na barra superior).
            </Alert>
          ) : (
            <RegraCard numero={idx + 1} rule={rule} fields={selectableFields}
              onChange={(patch) => updateRule(idx, patch)} onRemove={() => removeRule(idx)} />
          )}
        </React.Fragment>
      ))}

      <Button size="small" startIcon={<AddIcon />} sx={{ mt: 1.5 }}
        onClick={() => update({ rules: [...rules, emptyRule()] })}>
        Adicionar regra
      </Button>
    </Box>
  );
}

function Conector({ texto }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, my: 1 }} aria-hidden="true">
      <Box sx={{ flex: 1, borderTop: `1px dashed ${COR.primaryBorder}` }} />
      <Chip size="small" label={texto} sx={{
        height: 22, fontWeight: 700, fontSize: 11, letterSpacing: "0.06em",
        color: COR.primary, background: COR.primarySoft, border: `1px solid ${COR.primaryBorder}`,
      }} />
      <Box sx={{ flex: 1, borderTop: `1px dashed ${COR.primaryBorder}` }} />
    </Box>
  );
}

function RegraCard({ numero, rule, fields, onChange, onRemove }) {
  // React 16 não tem useId, e a Lógica monta várias regras na mesma tela: id fixo por instância.
  const idBase = React.useRef(`regra-${Math.random().toString(36).slice(2, 9)}`).current;
  const field = fields.find((f) => f.id === rule.field) || null;
  const hasOptions = !!field && Array.isArray(field.options);
  const operador = rule.operator || "eq";
  const needsValue = !NO_VALUE_OPS.includes(operador);
  const multiplo = hasOptions && MULTI_VALUE_OPS.includes(operador);

  const operadores = operadoresDoCampo(field);
  if (!operadores.includes(operador)) operadores.push(operador);

  const vazio = Array.isArray(rule.value)
    ? rule.value.length === 0
    : String(rule.value == null ? "" : rule.value).trim() === "";
  // O motor avalia regra sem pergunta ou sem resposta como falsa: num "todas as regras" a
  // pergunta some para todo paciente, num "qualquer regra" a regra não conta.
  const incompleta = !field || (needsValue && vazio);

  const trocarCampo = (novo) => {
    const ops = operadoresDoCampo(novo);
    // Resposta de uma pergunta não serve para outra: manter geraria regra que nunca casa.
    onChange({ field: novo ? novo.id : "", operator: ops.includes(operador) ? operador : ops[0], value: "" });
  };
  const trocarOperador = (op) => {
    const virouMultiplo = hasOptions && MULTI_VALUE_OPS.includes(op);
    let value = rule.value;
    if (NO_VALUE_OPS.includes(op)) value = "";
    else if (virouMultiplo && !Array.isArray(value)) value = value === "" || value == null ? [] : [value];
    else if (!virouMultiplo && Array.isArray(value)) value = value[0] || "";
    onChange({ operator: op, value });
  };

  return (
    <Box sx={{
      border: `1px solid ${incompleta ? COR.warningBorder : COR.borderSubtle}`,
      borderLeft: `3px solid ${incompleta ? COR.warning : COR.primary}`,
      borderRadius: "10px", p: 1.5, background: "#fff",
    }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", color: COR.textMuted, flex: 1 }}>
          REGRA {numero}
        </Typography>
        <IconButton size="small" color="error" onClick={onRemove} aria-label={`Remover regra ${numero}`}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>

      <Autocomplete
        size="small" options={fields} value={field}
        onChange={(_, novo) => trocarCampo(novo)}
        getOptionLabel={rotuloDoCampo}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        noOptionsText="Nenhuma pergunta encontrada"
        renderOption={(props, f) => (
          <li {...props} key={f.id}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ whiteSpace: "normal" }}>{rotuloDoCampo(f)}</Typography>
              <Typography variant="caption" sx={{ color: COR.textMuted }}>
                {(FIELD_TYPE_MAP[f.type] || {}).label || f.type}{f.name ? ` · ${f.name}` : ""}
              </Typography>
            </Box>
          </li>
        )}
        renderInput={(params) => <TextField {...params} label="Pergunta" placeholder="Busque pelo texto da pergunta" />}
      />

      <Box sx={{ display: "flex", gap: 1.5, mt: 1.5, flexWrap: "wrap" }}>
        <FormControl size="small" sx={{ flex: "1 1 180px" }}>
          <InputLabel id={`${idBase}-condicao`}>Condição</InputLabel>
          <Select labelId={`${idBase}-condicao`} label="Condição" value={operador} onChange={(e) => trocarOperador(e.target.value)}>
            {operadores.map((op) => (
              <MenuItem key={op} value={op}>{OPERATOR_LABELS[op] || op}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {needsValue && (hasOptions ? (
          <FormControl size="small" sx={{ flex: "2 1 240px", minWidth: 0 }}>
            <InputLabel id={`${idBase}-resposta`}>Resposta</InputLabel>
            <Select labelId={`${idBase}-resposta`} label="Resposta" multiple={multiplo}
              value={multiplo ? (Array.isArray(rule.value) ? rule.value : []) : (Array.isArray(rule.value) ? rule.value[0] || "" : rule.value || "")}
              onChange={(e) => onChange({ value: e.target.value })}
              MenuProps={{ PaperProps: { sx: { maxWidth: 480, maxHeight: 360 } } }}
              renderValue={(sel) => {
                const rotulo = (v) => (field.options.find((o) => o.value === v) || {}).label || v;
                return Array.isArray(sel) ? sel.map(rotulo).join(", ") : rotulo(sel);
              }}>
              {field.options.map((opt) => (
                <MenuItem key={opt.value} value={opt.value} sx={{ whiteSpace: "normal" }}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <TextField size="small" label="Resposta" sx={{ flex: "2 1 240px" }}
            type={field && ["number", "scale"].includes(field.type) ? "number" : "text"}
            value={rule.value == null ? "" : rule.value}
            onChange={(e) => onChange({ value: e.target.value })} />
        ))}
      </Box>

      {incompleta && (
        <Typography variant="caption" sx={{ color: COR.warning, display: "block", mt: 1 }}>
          Regra incompleta: escolha a pergunta{needsValue ? " e a resposta" : ""}. Enquanto isso ela conta como falsa.
        </Typography>
      )}
    </Box>
  );
}

function emptyRule() {
  return { field: "", operator: "eq", value: "" };
}
